// Summary: Shared action button — the single styled primitive for the green confirm/continue
// button (variant="confirm") and the white-with-red-text cancel button (variant="cancel") used
// across the whole system. loading swaps the label for a spinner; onClick runs behind a useRef
// re-entry lock so a same-tick double-tap fires once (Rule 44).
import React, { useRef } from 'react';
import classes from './button.module.scss';

export type ButtonVariant = 'confirm' | 'cancel' | 'save';

export type ButtonProps = {
  variant: ButtonVariant;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  fullWidth?: boolean;
  size?: ButtonSize;
  className?: string;
  ariaLabel?: string;
  dataTestId?: string;
};

export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  confirm: classes.confirm,
  cancel: classes.cancel,
  save: classes.save,
};

// md is the default footprint baked into `.button`; only sm/lg add an override class.
const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: classes.sm,
  md: '',
  lg: classes.lg,
};

const ButtonComponent: React.FC<ButtonProps> = ({
  variant,
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  loadingLabel,
  fullWidth = false,
  size = 'md',
  className,
  ariaLabel,
  dataTestId,
}) => {
  // Rule 44: the useRef re-entry lock is the authoritative guard against a same-tick double
  // fire, since disabled/loading derive from parent state that only updates a render later.
  const inFlightRef = useRef(false);
  const isBlocked = disabled || loading;

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isBlocked || inFlightRef.current || !onClick) {
      return;
    }
    inFlightRef.current = true;
    try {
      await onClick(event);
    } finally {
      inFlightRef.current = false;
    }
  };

  const classNames = [
    classes.button,
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    fullWidth ? classes.fullWidth : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classNames}
      onClick={handleClick}
      disabled={isBlocked}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      data-testid={dataTestId}
    >
      {loading ? (
        <span className={classes.loadingContent}>
          <span className={classes.spinner} aria-hidden="true" />
          {loadingLabel ? <span>{loadingLabel}</span> : null}
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export const Button = React.memo(ButtonComponent);
